// Ads › KOL — ฝัง KOL Report ของแคมเปญที่เลือก
//
// ฝังตรงไปโดเมนเขา (cross-origin) ไม่ผ่าน reverse proxy เพราะ frontend ฝั่งนั้น
// เรียก /api/... แบบ absolute ซึ่งโดเมนเราให้ Inbox Center ไปแล้ว
//
// **ต้องส่ง token ไปด้วย** แม้ ?view=1 จะเปิดหน้าได้โดยไม่ต้อง login: โหมดนั้นของเขา
// แปลว่า "ทีมงานที่ login อยู่ กำลังดูเลย์เอาต์แบบที่ลูกค้าเห็น" ไม่ได้แปลว่าไม่มี session
// ค่าตัว / บูส / KPI ที่ขาย อ่านจาก /api/roster ซึ่งต้องมี token — ไม่ส่งไปสามคอลัมน์นั้น
// จะหายไปทั้งคอลัมน์เงียบ ๆ (401 แล้ว component ไม่ render) เหลือ 11 คอลัมน์จาก 14
//
// ส่งทาง #token= ตามที่ฝั่งเขาเตรียมไว้ให้ระบบพี่น้อง (ดู ssoHandoff.ts ของ KOL):
// เขาเอาไปตรวจกับ /api/auth/profile แล้วเก็บเป็น session ของโดเมนเขาเอง พร้อมลบ
// ออกจาก URL ให้ · ใช้ hash ไม่ใช่ query เพราะ hash ไม่ถูกส่งไปกับ request ใด ๆ
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import type { Brand } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { CampaignSelect } from "@features/campaigns/components/campaign-select";
import { EmbedHead } from "@features/embeds/components/embed-head";
import { brandCfgLink } from "@features/navigation/model/side-menus";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { apiErrorMessage } from "@shared/api/client";
import { cn } from "@shared/lib/utils";
import { fetchKolCampaign, fetchKolRefreshStatus, kolKeys, startKolRefresh } from "../api/kol-api";

const KOL_REPORT_BASE = "https://fefl-kol-report.datafirst.id";

export function KolReportPage({ brand }: { brand: Brand }) {
  const [params] = useSearchParams();
  const { data: campaigns, isPending } = useCampaigns(brand.id);
  const list = campaigns ?? [];
  const campaign = list.find((c) => c.id === params.get("campaign"));

  // ผูกได้หลายรายงานต่อแคมเปญ → มีมากกว่า 1 ให้เลือกว่าจะดูอันไหน
  // มาจากตาราง Ads › Campaigns พร้อม ?ref= → เปิดรายงานตัวนั้นเลย ไม่ต้องกดสลับอีกรอบ
  const kolRefs = campaign?.kolRefs?.length
    ? campaign.kolRefs
    : campaign?.kolRef
      ? [campaign.kolRef]
      : [];
  const wantRef = params.get("ref");
  const [picked, setPicked] = useState<string | null>(null);
  const current = picked ?? (wantRef && kolRefs.includes(wantRef) ? wantRef : kolRefs[0]);

  const names = new Map(
    (campaign?.links ?? [])
      .filter((l) => l.kind === "kol")
      .map((l) => [l.refId, l.refName || l.refId]),
  );
  const plainUrl = `${KOL_REPORT_BASE}/c/${encodeURIComponent(current ?? "")}`;
  const refresh = useKolRefresh(current);
  // token ของคนที่เปิดหน้านี้อยู่ — ไม่มีก็ยังฝังได้ แค่ขาดคอลัมน์ฝั่งการเงิน
  const handoff = wazzupToken();

  return (
    <>
      <EmbedHead
        crumb={`${brand.name} / Ads / KOL`}
        title="KOL Report"
        right={
          <>
            <CampaignSelect campaigns={list} />
            {kolRefs.length > 1 && (
              <div className="embed-tabs kol-tabs">
                {kolRefs.map((ref) => (
                  <button
                    key={ref}
                    type="button"
                    className={cn("embed-tab", "kol-tab", ref === current && "active")}
                    onClick={() => setPicked(ref)}
                  >
                    {names.get(ref) || ref}
                  </button>
                ))}
              </div>
            )}
          </>
        }
        note={
          campaign && current ? (
            <>
              แคมเปญ: {campaign.name} <span className="code-chip">{current}</span> ·{" "}
              <a href={plainUrl} target="_blank" rel="noopener">
                เปิดในแท็บใหม่ ↗
              </a>
              {" · "}
              {/* ตัวเลขในรายงานคือรอบที่ดึงล่าสุด ไม่ใช่สด — ไม่บอกอายุไว้ก็ตีความผิดได้ */}
              <span className={cn(refresh.stale && "kol-stale")}>
                ดึงข้อมูลล่าสุด {refresh.since || "—"}
              </span>{" "}
              <button
                type="button"
                className="link-btn"
                disabled={refresh.busy}
                onClick={refresh.run}
              >
                {refresh.busy ? "กำลังดึงข้อมูล…" : "ดึงข้อมูลใหม่"}
              </button>
              {refresh.msg && <span className="muted"> · {refresh.msg}</span>}
            </>
          ) : undefined
        }
      />
      <div className="embed-frame">
        {isPending ? (
          <div className="embed-empty">
            <p className="muted">กำลังโหลดแคมเปญ…</p>
          </div>
        ) : !campaign ? (
          // ต้องเลือกแคมเปญก่อน เพราะรายงาน KOL ผูกกับแคมเปญเดียวเสมอ
          <div className="embed-empty">
            <h3>เลือกแคมเปญก่อน</h3>
            <p className="muted">
              {list.length ? (
                "รายงาน KOL แสดงทีละแคมเปญ — เลือกแคมเปญจาก dropdown ด้านบน"
              ) : (
                <>
                  แบรนด์นี้ยังไม่มีแคมเปญ — สร้างได้ที่{" "}
                  <a href={brandCfgLink(brand)}>หน้าตั้งค่า → Campaigns</a>
                </>
              )}
            </p>
          </div>
        ) : !current ? (
          <div className="embed-empty">
            <h3>ยังไม่ได้ผูกรหัส KOL Report</h3>
            <p className="muted">
              แคมเปญ &quot;{campaign.name}&quot; ยังไม่มีรหัสฝั่ง KOL Report
              <br />
              ใส่รหัส (เช่น <code>00001</code>) ได้ที่{" "}
              <a href={brandCfgLink(brand)}>หน้าตั้งค่า → Campaigns</a>
            </p>
          </div>
        ) : (
          // ?view=1 = โหมด View Only for Client ของ KOL Report เอง — ซ่อน navbar และ
          // ปุ่มแก้ไข/PowerPoint/Refresh/Share ให้หมด เหลือแต่เนื้อรายงาน จึงกลมกลืน
          // กับหน้าเรา และโหมดนี้ไม่ต้อง login (auth guard ฝั่งเขาข้ามให้) → ไม่ต้องส่ง token
          <iframe
            // nonce เปลี่ยน = React ถอด iframe เก่าทิ้งแล้วสร้างใหม่ ทำให้รายงาน
            // โหลดตัวเลขรอบล่าสุด (reload ผ่าน contentWindow ทำไม่ได้ คนละโดเมน)
            key={`${current}:${refresh.nonce}`}
            src={
              `${KOL_REPORT_BASE}/c/${encodeURIComponent(current)}?view=1` +
              (handoff ? `#token=${encodeURIComponent(handoff)}` : "")
            }
            title="KOL Report"
            referrerPolicy="no-referrer"
          />
        )}
      </div>
    </>
  );
}

/** อายุข้อมูลแบบอ่านง่าย — ตัวเลขที่ไม่รู้ว่าดึงเมื่อไรก็ตีความไม่ได้ */
function since(iso: string, now: number): string {
  const mins = Math.floor((now - Date.parse(iso)) / 60_000);
  if (!isFinite(mins)) return "";
  if (mins < 1) return "เมื่อครู่นี้";
  if (mins < 60) return `${mins} นาทีก่อน`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ชม.ก่อน`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "เมื่อวาน" : `${days} วันก่อน`;
}

/** ข้อมูลเก่ากว่านี้ = เตือนด้วยสี ไม่ใช่ปล่อยให้อ่านเป็นตัวเลขวันนี้ */
const STALE_HOURS = 24;

/**
 * ปุ่มดึงข้อมูลใหม่ของรายงานที่ฝังอยู่
 *
 * รายงานที่ฝังเป็นโหมด view-only ของฝั่ง KOL ซึ่งซ่อนแถบปุ่มทั้งแถบ รวมถึงปุ่มดึง
 * ข้อมูลใหม่ — ตัวเลขจึงค้างที่รอบล่าสุดที่มีคนไปกดในระบบเขาเอง hook นี้ยิงคำสั่งนั้น
 * ผ่าน relay ของเรา แล้วถามความคืบหน้าเป็นรอบ ๆ จนเสร็จ ค่อยสร้าง iframe ใหม่
 */
function useKolRefresh(campaign: string | undefined) {
  const qc = useQueryClient();
  // เวลาอ้างอิงตั้งครั้งเดียวตอน mount — อ่านนาฬิกาตอน render เป็นผลข้างเคียง
  // ที่ทำให้ผลลัพธ์ของ render ไม่คงที่ (และ lint ห้ามไว้) · ป้ายอายุข้อมูลไม่ต้อง
  // เดินวินาทีต่อวินาที เปิดหน้าใหม่หรือดึงข้อมูลเสร็จก็ได้ค่าใหม่แล้ว
  const [now] = useState(() => Date.now());
  const [nonce, setNonce] = useState(0);
  const [msg, setMsg] = useState("");
  const [watching, setWatching] = useState(false);

  const meta = useQuery({
    queryKey: kolKeys.campaign(campaign ?? ""),
    queryFn: () => fetchKolCampaign(campaign!),
    enabled: !!campaign,
    staleTime: 60_000,
  });

  const job = useQuery({
    queryKey: kolKeys.refresh(campaign ?? ""),
    queryFn: () => fetchKolRefreshStatus(campaign!),
    enabled: !!campaign && watching,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 4000 : false),
  });

  // งานเสร็จแล้ว — โหลดรายงานใหม่ทีเดียว แล้วเลิกถามความคืบหน้า
  const done = watching && job.data && job.data.status !== "running";
  if (done) {
    setWatching(false);
    setNonce((n) => n + 1);
    setMsg(job.data?.status === "error" ? job.data.message || "ดึงข้อมูลไม่สำเร็จ" : "");
    void qc.invalidateQueries({ queryKey: kolKeys.campaign(campaign ?? "") });
  }

  const start = useMutation({
    mutationFn: () => startKolRefresh(campaign!),
    onSuccess: () => {
      setMsg("");
      setWatching(true);
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
  });

  const at = meta.data?.refreshedAt ?? "";
  const age = at ? now - Date.parse(at) : NaN;

  return {
    nonce,
    msg,
    since: at ? since(at, now) : "",
    stale: isFinite(age) && age > STALE_HOURS * 3_600_000,
    busy: start.isPending || watching || job.data?.status === "running",
    run: () => {
      if (!campaign) return;
      // Apify คิดเงินต่อรายการ — ถามก่อนเหมือนปุ่มดึงยอด follower ในหน้า Brand
      const ok = window.confirm(
        `ให้ระบบ KOL Report ไปดึงยอดใหม่ของแคมเปญนี้จาก Apify\n\n` +
          `การดึงข้อมูลมีค่าใช้จ่ายต่อโพสต์ และใช้เวลาสักครู่ — ยืนยันเพื่อเริ่ม`,
      );
      if (ok) start.mutate();
    },
  };
}
